import DropdownMenu from 'components/common/Dropdown/DropdownMenu'
import { faShare, faTimes } from '@fortawesome/pro-regular-svg-icons'
import {
  CollectiveQueryResult,
  CollectiveProposal,
  CollectiveProposalStatus,
  CollectiveProposalQueryResult,
  CollectiveProposalType
} from 'graphql/generated'
import { useCurrentUser } from 'hooks'
import { CancelProposalModal } from '../modals/CancelProposalModal'
import { useState } from 'react'
import LinkShareContent from '../../common/Share/LinkShareContent'
import Typography from 'components/common/Typography'
import ClickAwayListener from 'components/common/ClickAwayListener'

interface IProps {
  collective: Pick<
    NonNullable<CollectiveQueryResult['collective']>,
    'id' | 'address' | 'tokens' | 'membership' | 'name'
  >
  proposal: Pick<
    CollectiveProposal,
    | 'id'
    | 'title'
    | 'status'
    | 'isBatch'
    | 'isPartOfBatch'
    | 'canVote'
    | 'myVote'
    | 'type'
    | 'publicUrl'
    | 'title'
  > & {
    proposedBy: {
      user: {
        id: string
      }
    }
  } & {
    collective: Pick<
      NonNullable<
        CollectiveProposalQueryResult['collectiveProposal']
      >['collective'],
      'id'
    >
  }
}

function ProposalMoreActionsButton({ collective, proposal }: IProps) {
  const { collectiveUser } = useCurrentUser()

  const isViewerTheAuthor = collectiveUser?.some(
    x => x.id === proposal.proposedBy.user.id
  )
  const isSignator = collective.membership?.privileged || false
  const canCancelProposal =
    !proposal.isBatch &&
    !proposal.isPartOfBatch &&
    [
      CollectiveProposalStatus.PENDING,
      CollectiveProposalStatus.ACTIVE,
      ...(proposal.type === CollectiveProposalType.COMMANDS
        ? [CollectiveProposalStatus.SUCCEEDED]
        : [])
    ].includes(proposal.status) &&
    (isViewerTheAuthor || isSignator)

  const [showCancelProposalModal, setShowCancelProposalModal] = useState(false)
  const [showShareProposal, setShowShareProposal] = useState(false)

  const items = [
    {
      label: 'Share Proposal',
      icon: faShare,
      onClick: () => {
        window.analytics.track(
          'Clicked Share Proposal in Proposal Screen More Options',
          {
            proposalId: proposal.id,
            proposalName: proposal.title
          }
        )
        setShowShareProposal(true)
      }
    }
  ]

  if (canCancelProposal) {
    items.push({
      label: 'Cancel Proposal',
      icon: faTimes,
      onClick: () => setShowCancelProposalModal(true)
    })
  }

  const collectiveNameForCopy = collective.name.toLowerCase().endsWith('dao')
    ? collective.name
    : `${collective.name} DAO`

  return (
    <div className="ml-2 relative">
      <DropdownMenu
        items={items}
        onClick={() => {
          window.analytics.track(
            'Clicked More Options Button on Proposal Screen',
            {
              proposalId: proposal.id,
              proposalName: proposal.title
            }
          )
        }}
      />

      {showCancelProposalModal && (
        <CancelProposalModal
          proposal={proposal}
          onClose={() => setShowCancelProposalModal(false)}
        />
      )}

      {showShareProposal && (
        <ClickAwayListener onClickAway={() => setShowShareProposal(false)}>
          <div className="mt-2 absolute right-0 bg-white border border-gray-200 shadow-lg rounded-lg z-4000 overflow-visible">
            <LinkShareContent
              titleContent={
                <Typography className="mb-4">
                  Share your proposal with other members &#127881;
                </Typography>
              }
              url={proposal.publicUrl}
              trackEventProps={{
                proposalId: proposal.id,
                proposalName: proposal.title
              }}
              onClose={() => setShowShareProposal(false)}
              extraCopyText={`${collectiveNameForCopy}: Please review and vote on this proposal!\r\n\r\n`}
            />
          </div>
        </ClickAwayListener>
      )}
    </div>
  )
}

export default ProposalMoreActionsButton
